/**
 * Graph API Service
 * Handles API calls for graph data and real-time updates
 */

const ENV_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let BASE_URL =
  ENV_BASE_URL ||
  (process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:8012");

const GRAPH_API_URL = BASE_URL + '/v2/travelagent/graph';
const GRAPH_STREAM_API_URL = BASE_URL + '/v2/travelagent/graph/stream';

/**
 * Fetch current graph state
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Graph data with nodes and edges
 */
export const fetchGraphData = async (sessionId, userId) => {
  try {
    const response = await fetch(GRAPH_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: sessionId || null,
        user_id: userId || null
      })
    });

    if (!response.ok) {
      // If endpoint doesn't exist, return null (will use default graph)
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || 'Request failed');
    }

    return await response.json();
  } catch (error) {
    // Return null on error (will use default graph)
    console.warn('Graph API not available, using default graph:', error);
    return null;
  }
};

/**
 * Subscribe to real-time graph updates via Server-Sent Events
 * @param {string} sessionId - Session ID
 * @param {Function} onUpdate - Callback function for updates: (update) => void
 * @returns {Function} Cleanup function to unsubscribe
 */
export const subscribeToGraphUpdates = (sessionId, onUpdate) => {
  if (!sessionId) {
    console.warn('No sessionId provided for graph updates');
    return () => {};
  }

  let eventSource = null;
  let reconnectTimeout = null;
  let isClosed = false;

  const connect = () => {
    if (isClosed) return;

    try {
      // Use EventSource for SSE
      const url = `${GRAPH_STREAM_API_URL}?session_id=${encodeURIComponent(sessionId)}`;
      eventSource = new EventSource(url);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onUpdate) {
            onUpdate(data);
          }
        } catch (error) {
          console.error('Failed to parse graph update:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('Graph stream error:', error);
        eventSource.close();
        
        // Reconnect after 3 seconds
        if (!isClosed) {
          reconnectTimeout = setTimeout(() => {
            connect();
          }, 3000);
        }
      };

      eventSource.onopen = () => {
        console.log('Graph stream connected');
      };
    } catch (error) {
      console.error('Failed to connect to graph stream:', error);
      // Retry connection
      if (!isClosed) {
        reconnectTimeout = setTimeout(() => {
          connect();
        }, 3000);
      }
    }
  };

  // Start connection
  connect();

  // Return cleanup function
  return () => {
    isClosed = true;
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }
    if (eventSource) {
      eventSource.close();
    }
  };
};

/**
 * Send graph event (e.g., node activation, message)
 * @param {Object} event - Event object
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Response data
 */
export const sendGraphEvent = async (event, sessionId, userId) => {
  try {
    const response = await fetch(GRAPH_API_URL + '/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event: event,
        session_id: sessionId || null,
        user_id: userId || null
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || 'Request failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to send graph event:', error);
    throw error;
  }
};

/**
 * Fetch detailed logs for a specific node
 * @param {string} nodeId - Node ID
 * @param {string} sessionId - Session ID (optional)
 * @returns {Promise<Object>} Logs data with node_id, logs array, and count
 */
export const fetchNodeLogs = async (nodeId, sessionId) => {
  try {
    const url = new URL(`${GRAPH_API_URL}/logs/${encodeURIComponent(nodeId)}`);
    if (sessionId) {
      url.searchParams.append('session_id', sessionId);
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || 'Request failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to fetch node logs:', error);
    throw error;
  }
};

